import { t } from '@lingui/core/macro';
import { Plural, Trans } from '@lingui/react/macro';

import { useTrafficDetailKpis } from '@/api/common';
import { KpiMetric, KpiMetricGroup } from '@/components/kpi';
import { usePageFilterContext } from '@/components/page';
import { SupportLink } from '@/components/typography';
import { numberFormat } from '@/helper/numberFormatter';

type Props = {
  objectType: 'vendor' | 'campaign';
  vendorIds?: string[];
  campaignIds?: string[];
  isLoading?: boolean;
};

const TrafficDetailKPIMetrics = ({
  objectType,
  vendorIds,
  campaignIds,
  isLoading: isObjectLoading,
}: Props) => {
  const {
    pageFilters: { startDate, endDate, audience },
  } = usePageFilterContext();

  const {
    kpiData: data,
    isLoading: isTrafficKPIsLoading,
    error,
  } = useTrafficDetailKpis({
    startDate,
    endDate,
    vendorIds,
    campaignIds,
    audienceIds: audience?.id,
  });

  const isLoading = isObjectLoading || isTrafficKPIsLoading;
  const objectTypeLabel = objectType === 'vendor' ? t`vendor` : t`campaign`;

  return (
    <KpiMetricGroup>
      <KpiMetric
        label={t`Total Visits`}
        value={data && numberFormat(data?.visits)}
        caption={data && <Trans>Previous Period: {numberFormat(data?.visitsLastPeriod)}</Trans>}
        percentChange={data?.visitsPercentChange}
        isLoading={isLoading}
        error={error}
      />
      <KpiMetric
        label={t`Visit Efficiency`}
        infoPopover={{
          body: (
            <Trans>
              Visit efficiency is the ratio of target audience visits to total visits (unknown
              removed) to give a view of how well a {objectTypeLabel} attracts the right audience.{' '}
              <SupportLink variant="body2" color="static-green" path="VISIT_EFFICIENCY">
                More Details
              </SupportLink>
            </Trans>
          ),
        }}
        value={data && numberFormat(data?.percentEfficiency, { isPercent: true, precision: 1 })}
        isLoading={isLoading}
        error={error}
      />
      <KpiMetric
        label={t`Engaged Companies`}
        value={data && numberFormat(data?.engagedCompanies)}
        caption={
          data && <Trans>Previous Period: {numberFormat(data?.engagedCompaniesLastPeriod)}</Trans>
        }
        percentChange={data?.engagedCompaniesPercentChange}
        isLoading={isLoading}
        error={error}
      />
      <KpiMetric
        label={t`ROMS`}
        infoPopover={{
          body: t`Return on marketing spend is calculated by calculating Pipeline divided by spend.`,
        }}
        value={data && numberFormat(data?.roms, { isMultiplier: true, precision: 1 })}
        caption={
          data && (
            <Trans>
              {numberFormat(data?.pipelineInfluenced, { isCurrency: true, precision: 0 })} /{' '}
              {numberFormat(data?.spend, { isCurrency: true, precision: 0 })}
            </Trans>
          )
        }
        isLoading={isLoading}
        error={error}
      />
      <KpiMetric
        label={t`Pipeline Influence`}
        value={data && numberFormat(data?.pipelineInfluenced, { isCurrency: true, precision: 0 })}
        caption={
          data?.pipelineOpportunities != null && (
            <Plural
              value={data?.pipelineOpportunities + ''}
              one="# Opportunity"
              other="# Opportunities"
            />
          )
        }
        isLoading={isLoading}
        error={error}
      />
      <KpiMetric
        label={t`Closed Won Influence`}
        value={data && numberFormat(data?.closedWonInfluenced, { isCurrency: true, precision: 0 })}
        caption={
          data?.closedWonOpportunities != null && (
            <Plural
              value={data?.closedWonOpportunities + ''}
              one="# Opportunity"
              other="# Opportunities"
            />
          )
        }
        isLoading={isLoading}
        error={error}
      />
    </KpiMetricGroup>
  );
};

export default TrafficDetailKPIMetrics;
